import { useQuery } from '@tanstack/react-query';
import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  Option,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Input,
  IconButton,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Modal,
  ModalDialog,
  ModalClose,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Sheet,
} from '@mui/joy';
import {
  Plus,
  PencilSimple as Edit2,
  Trash as Trash2,
  FloppyDisk as Save,
  X,
  DotsSixVertical as GripVertical,
  Layout,
  Gear as Settings,
  ArrowLeft,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DraggablePortal } from '../components/common/DraggablePortal';
import { apiClient } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';

interface Stage {
  job_count?: number;
  id: number;
  name: string;
  order: number;
  board: number;
}

interface Board {
  job_count?: number;
  id: number;
  name: string;
  color?: string;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

const JobBoardSettings: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loadingStages, setLoadingStages] = useState(false);
  
  // Edit states
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardColor, setNewBoardColor] = useState('#3B82F6');
  const [newStageName, setNewStageName] = useState('');
  
  // Modal states
  const [showNewBoardModal, setShowNewBoardModal] = useState(false);
  const [showNewStageModal, setShowNewStageModal] = useState(false);
  const [showDeleteBoardModal, setShowDeleteBoardModal] = useState(false);
  const [showDeleteStageModal, setShowDeleteStageModal] = useState(false);
  const [stageToDelete, setStageToDelete] = useState<Stage | null>(null);

  const stageVersion = useRef(0);
  const activeBoard = useRef(selectedBoard?.id);
  activeBoard.current = selectedBoard?.id;
  const [destinationStage, setDestinationStage] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { data: destinationBoards } = useQuery({
    queryKey: ['deletion-destinations', selectedBoard?.id, showDeleteBoardModal, showDeleteStageModal],
    enabled: showDeleteBoardModal || showDeleteStageModal,
    queryFn: async () => (await apiClient.get<any>('/api/boards/with-statuses/')).data.boards,
  });
  const destinationPicker = <Box sx={{ my: 2 }}>
    <Typography level="body-sm">Destination for existing jobs</Typography>
    <Select placeholder="Choose a destination stage" value={destinationStage} onChange={(_, value) => setDestinationStage(value)}>
      {(destinationBoards || []).flatMap((board: any) => board.statuses.filter((stage: any) => showDeleteBoardModal ? board.id !== selectedBoard?.id : stage.id !== stageToDelete?.id).map((stage: any) => <Option key={stage.id} value={stage.id}>{board.name} / {stage.name}</Option>))}
    </Select>
  </Box>;
  useEffect(() => { setDestinationStage(null); }, [showDeleteBoardModal, showDeleteStageModal]);

  // Fetch boards on mount
  useEffect(() => {
    fetchBoards();
  }, []);

  // Fetch stages when board is selected
  useEffect(() => {
    if (selectedBoard) {
      fetchStages(selectedBoard.id);
    }
  }, [selectedBoard]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const response = await apiClient.getAllPages<Board>('/api/boards/');
      const boardsData = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      setBoards(boardsData);
      
      // Select the first board by default
      setSelectedBoard(previous => boardsData.find((board: Board) => board.id === previous?.id) || boardsData[0] || null);
    } catch (error) {
      showError('Failed to load job boards');
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchStages = async (boardId: number) => {
    const version = ++stageVersion.current;
    setStages([]);
    try {
      setLoadingStages(true);
      const response = await apiClient.getAllPages<Stage>(`/api/board-statuses/?board=${boardId}`);
      const stagesData = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      // Sort by order
      const sortedStages = stagesData.sort((a: Stage, b: Stage) => (a.order || 0) - (b.order || 0));
      if (version !== stageVersion.current || activeBoard.current !== boardId) return;
      setStages(sortedStages);
    } catch (error) {
      if (version === stageVersion.current && activeBoard.current === boardId) showError('Failed to load stages');
    } finally {
      if (version === stageVersion.current && activeBoard.current === boardId) setLoadingStages(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) {
      showError('Board name is required');
      return;
    }

    try {
      const response = await apiClient.post<Board>('/api/boards/', {
        name: newBoardName.trim(),
        color: newBoardColor,
        // organization is automatically set by backend from authenticated user
      });
      
      const newBoard = response.data;
      setBoards([...boards, newBoard]);
      setSelectedBoard(newBoard);
      setNewBoardName('');
      setNewBoardColor('#3B82F6');
      setShowNewBoardModal(false);
      showSuccess('Board created successfully');
    } catch (error: any) {
      // The API interceptor will handle upgrade_required errors
      // Only show generic error for other types of errors
      if (error.response?.data?.upgrade_required) {
        // Don't show generic error - let the interceptor handle it
        console.log('Usage limit exceeded - upgrade prompt will be shown');
      } else {
        showError('Failed to create board');
      }
    }
  };

  const handleUpdateBoard = async (board: Board) => {
    if (!editingBoard || !editingBoard.name.trim()) {
      showError('Board name is required');
      return;
    }

    try {
      const response = await apiClient.patch<Board>(`/api/boards/${board.id}/`, {
        name: editingBoard.name.trim(),
        color: editingBoard.color,
      });
      
      const updatedBoard = response.data;
      setBoards(boards.map(b => b.id === board.id ? updatedBoard : b));
      if (selectedBoard?.id === board.id) {
        setSelectedBoard(updatedBoard);
      }
      setEditingBoard(null);
      showSuccess('Board updated successfully');
    } catch (error) {
      showError('Failed to update board');
    }
  };

  const handleDeleteBoard = async () => {
    if (!selectedBoard) return;

    if (deleting) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/boards/${selectedBoard.id}/${destinationStage ? `?destination_stage=${destinationStage}` : ''}`);
      
      const remainingBoards = boards.filter(b => b.id !== selectedBoard.id);
      setBoards(remainingBoards);
      setSelectedBoard(remainingBoards.length > 0 ? remainingBoards[0] : null);
      setStages([]);
      setShowDeleteBoardModal(false);
      showSuccess('Board deleted successfully');
      await fetchBoards();
    } catch (error) {
      showError('Board was not deleted. Select a valid destination for its jobs and retry.');
    } finally { setDeleting(false); }
  };

  const handleCreateStage = async () => {
    if (!newStageName.trim() || !selectedBoard) {
      showError('Stage name is required');
      return;
    }

    try {
      const maxOrder = Math.max(...stages.map(s => s.order || 0), 0);
      const response = await apiClient.post<Stage>('/api/board-statuses/', {
        name: newStageName.trim(),
        board: selectedBoard.id,
        order: maxOrder + 1,
      });
      
      const newStage = response.data;
      setStages([...stages, newStage]);
      setNewStageName('');
      setShowNewStageModal(false);
      showSuccess('Stage created successfully');
    } catch (error) {
      showError('Failed to create stage');
    }
  };

  const handleUpdateStage = async (stage: Stage) => {
    if (!editingStage || !editingStage.name.trim()) {
      showError('Stage name is required');
      return;
    }

    try {
      const response = await apiClient.patch<Stage>(`/api/board-statuses/${stage.id}/`, {
        name: editingStage.name.trim(),
      });
      
      const updatedStage = response.data;
      setStages(stages.map(s => s.id === stage.id ? updatedStage : s));
      setEditingStage(null);
      showSuccess('Stage updated successfully');
    } catch (error) {
      showError('Failed to update stage');
    }
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;

    if (deleting) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/board-statuses/${stageToDelete.id}/${destinationStage ? `?destination_stage=${destinationStage}` : ''}`);
      
      setStages(stages.filter(s => s.id !== stageToDelete.id));
      setStageToDelete(null);
      setShowDeleteStageModal(false);
      showSuccess('Stage deleted successfully');
      await fetchBoards();
    } catch (error) {
      showError('Failed to delete stage. It may have jobs assigned.');
    } finally { setDeleting(false); }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order values
    const updatedStages = items.map((stage, index) => ({
      ...stage,
      order: index,
    }));

    setStages(updatedStages);

    // Update order on backend
    try {
      await apiClient.post(`/api/boards/${selectedBoard?.id}/reorder-stages/`, { ids: updatedStages.map(stage => stage.id) });
      showSuccess('Stage order updated successfully');
    } catch (error) {
      showError('Failed to update stage order');
      // Revert on error
      if (selectedBoard) {
        fetchStages(selectedBoard.id);
      }
    }
  };

  if (loadingBoards) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: 'background.body' }}>
      {/* Header */}
      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 0,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowLeft size={16} />}
              onClick={() => navigate('/operations/jobs')}
            >
              Back to Board
            </Button>
            <Typography level="h2" component="h1">
              Job Board Settings
            </Typography>
          </Box>
        </Box>
      </Sheet>

      <Box sx={{ p: 3, display: 'flex', gap: 3, height: 'calc(100vh - 120px)' }}>
        {/* Boards List */}
        <Card variant="outlined" sx={{ width: 350, overflow: 'auto' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography level="title-md" startDecorator={<Layout size={20} />}>
                Job Boards
              </Typography>
              <Button
                size="sm"
                startDecorator={<Plus size={16} />}
                onClick={() => setShowNewBoardModal(true)}
              >
                New Board
              </Button>
            </Box>

            <List>
              {boards.map(board => (
                <ListItem
                  key={board.id}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: selectedBoard?.id === board.id ? 'primary.softBg' : 'transparent',
                    borderRadius: 'sm',
                    mb: 0.5,
                  }}
                  onClick={() => setSelectedBoard(board)}
                >
                  {editingBoard?.id === board.id ? (
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      <Input
                        value={editingBoard.name}
                        onChange={(e) => setEditingBoard({ ...editingBoard, name: e.target.value })}
                        size="sm"
                        sx={{ flex: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Input
                        type="color"
                        value={editingBoard.color || '#3B82F6'}
                        onChange={(e) => setEditingBoard({ ...editingBoard, color: e.target.value })}
                        size="sm"
                        sx={{ width: 50 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <IconButton
                        size="sm"
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateBoard(board);
                        }}
                      >
                        <Save size={16} />
                      </IconButton>
                      <IconButton
                        size="sm"
                        color="neutral"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingBoard(null);
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemDecorator>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 'xs',
                            bgcolor: board.color || '#3B82F6',
                          }}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm">{board.name} ({board.job_count || 0} jobs)</Typography>
                        {board.is_default && (
                          <Chip size="sm" variant="soft" color="primary">
                            Default
                          </Chip>
                        )}
                      </ListItemContent>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingBoard(board);
                          }}
                        >
                          <Edit2 size={14} />
                        </IconButton>
                        {!board.is_default && (
                          <IconButton
                            size="sm"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBoard(board);
                              setShowDeleteBoardModal(true);
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        )}
                      </Box>
                    </>
                  )}
                </ListItem>
              ))}
            </List>

            {boards.length === 0 && (
              <Alert color="neutral">
                No boards found. Create your first board to get started.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Stages Management */}
        <Card variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
          <CardContent>
            {selectedBoard ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography level="title-md" startDecorator={<Settings size={20} />}>
                      Pipeline Stages
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                      Board: {selectedBoard.name}
                    </Typography>
                  </Box>
                  <Button
                    size="sm"
                    startDecorator={<Plus size={16} />}
                    onClick={() => setShowNewStageModal(true)}
                  >
                    Add Stage
                  </Button>
                </Box>

                {loadingStages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : stages.length > 0 ? (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="stages">
                      {(provided) => (
                        <List
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          sx={{ gap: 1 }}
                        >
                          {stages.map((stage, index) => (
                            <Draggable
                              key={stage.id}
                              draggableId={stage.id.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <DraggablePortal snapshot={snapshot}>
                                  <ListItem
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    sx={{
                                      bgcolor: snapshot.isDragging ? 'primary.softBg' : 'background.surface',
                                      borderRadius: 'sm',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      mb: 1,
                                    }}
                                  >
                                    <ListItemDecorator {...provided.dragHandleProps}>
                                      <GripVertical size={16} />
                                    </ListItemDecorator>
                                  
                                    {editingStage?.id === stage.id ? (
                                      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                        <Input
                                          value={editingStage.name}
                                          onChange={(e) => setEditingStage({ ...editingStage, name: e.target.value })}
                                          size="sm"
                                          sx={{ flex: 1 }}
                                        />
                                        <IconButton
                                          size="sm"
                                          color="success"
                                          onClick={() => handleUpdateStage(stage)}
                                        >
                                          <Save size={16} />
                                        </IconButton>
                                        <IconButton
                                          size="sm"
                                          color="neutral"
                                          onClick={() => setEditingStage(null)}
                                        >
                                          <X size={16} />
                                        </IconButton>
                                      </Box>
                                    ) : (
                                      <>
                                        <ListItemContent>
                                          <Typography level="body-sm" fontWeight="md">
                                            {stage.name} ({stage.job_count || 0} jobs)
                                          </Typography>
                                          <Typography level="body-xs" color="neutral">
                                            Position: {index + 1}
                                          </Typography>
                                        </ListItemContent>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <IconButton
                                            size="sm"
                                            onClick={() => setEditingStage(stage)}
                                          >
                                            <Edit2 size={14} />
                                          </IconButton>
                                          <IconButton
                                            size="sm"
                                            color="danger"
                                            onClick={() => {
                                              setStageToDelete(stage);
                                              setShowDeleteStageModal(true);
                                            }}
                                          >
                                            <Trash2 size={14} />
                                          </IconButton>
                                        </Box>
                                      </>
                                    )}
                                  </ListItem>
                                </DraggablePortal>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </List>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  <Alert color="neutral">
                    No stages defined for this board. Add stages to define your job pipeline.
                  </Alert>
                )}

                <Divider sx={{ my: 3 }} />
                
                <Alert color="primary">
                  <Typography level="body-sm">
                    <strong>Tips:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>Drag and drop stages to reorder them. The board will follow the order shown above.</li>
                      <li>Click the edit icon to rename a stage</li>
                      <li>Jobs will move through these stages in your pipeline</li>
                      <li>Deleting a stage with jobs will require reassigning them</li>
                    </ul>
                  </Typography>
                </Alert>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <Typography level="body-md" color="neutral">
                  Select a board to manage its stages
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* New Board Modal */}
      <Modal open={showNewBoardModal} onClose={() => setShowNewBoardModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Create New Board</Typography>
          <Box sx={{ mt: 2 }}>
            <Input
              placeholder="Board name"
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography level="body-sm">Color:</Typography>
              <Input
                type="color"
                value={newBoardColor}
                onChange={(e) => setNewBoardColor(e.target.value)}
                sx={{ width: 100 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setShowNewBoardModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateBoard}>
                Create Board
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* New Stage Modal */}
      <Modal open={showNewStageModal} onClose={() => setShowNewStageModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Add New Stage</Typography>
          <Box sx={{ mt: 2 }}>
            <Input
              placeholder="Stage name"
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setShowNewStageModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateStage}>
                Add Stage
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Delete Board Confirmation */}
      <Modal open={showDeleteBoardModal} onClose={() => setShowDeleteBoardModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete Board</Typography>
          <Typography level="body-md" sx={{ mt: 2 }}>
            Are you sure you want to delete the board "{selectedBoard?.name}"? 
            {selectedBoard?.job_count || 0} jobs will be preserved and reassigned. This action cannot be undone.
          </Typography>
          {destinationPicker}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => setShowDeleteBoardModal(false)}>
              Cancel
            </Button>
            <Button color="danger" loading={deleting} disabled={deleting || (!!selectedBoard?.job_count && !destinationStage)} onClick={handleDeleteBoard}>
              Delete Board
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Delete Stage Confirmation */}
      <Modal open={showDeleteStageModal} onClose={() => setShowDeleteStageModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete Stage</Typography>
          <Typography level="body-md" sx={{ mt: 2 }}>
            Are you sure you want to delete the stage "{stageToDelete?.name}"? 
            {stageToDelete?.job_count || 0} jobs must be reassigned before this stage can be deleted.
          </Typography>
          {destinationPicker}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => setShowDeleteStageModal(false)}>
              Cancel
            </Button>
            <Button color="danger" loading={deleting} disabled={deleting || (!!stageToDelete?.job_count && !destinationStage)} onClick={handleDeleteStage}>
              Delete Stage
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default JobBoardSettings;